/**
 * v-b-tooltip directive - replaces bootstrap-vue's, with the same markup, timings and behaviour, but only
 * for what the app uses (pinned by __tests__/bootstrap/vBTooltipUsage.test.js):
 *
 *   v-b-tooltip                      shown on hover and focus
 *   v-b-tooltip.hover                shown on hover
 *   v-b-tooltip.hover.html           the title is rendered as HTML
 *   v-b-tooltip.noninteractive.hover the tooltip itself ignores the mouse
 *
 * The text is the `title` attribute of the element. Placement is always "top" (flips when there is no room).
 * Reference: node_modules/bootstrap-vue/src/directives/tooltip and .../components/tooltip/helpers.
 */
// popper.js v1 with bootstrap-vue's own settings: the tooltip lands on exactly the same pixels as before
// (@popperjs/core v2 rounds differently and uses another default boundary)
import Popper from "popper.js"

const DATA_TITLE_ATTR = 'data-original-title'
// same values as bootstrap-vue / bootstrap
const DELAY = 50 // ms before showing after the pointer enters and before hiding after it leaves
const FADE_DURATION = 150 // .fade { transition: opacity .15s linear }
const VISIBILITY_CHECK_INTERVAL = 100
const BOUNDARY_PADDING = 5
// a tooltip of an element inside a modal is rendered inside the modal
const CONTAINER_SELECTOR = '.modal-content, .b-sidebar'

let uid = 0

const isDisabled = (el) => el.disabled || el.hasAttribute('disabled') || el.classList.contains('disabled')

// a modal opened over the element (e.g. by tapping it: on touch screens mouseleave comes only with the next tap elsewhere)
const isCoveredByModal = (el) => {
  const modals = document.querySelectorAll('.modal')
  if(!modals.length) return false
  // the modal opened last is on top (each one is teleported to the end of body when shown)
  return !modals[modals.length - 1].contains(el)
}

const isVisible = (el) => {
  if(!el.parentNode || !document.body.contains(el)) return false
  if(isCoveredByModal(el)) return false
  if(getComputedStyle(el).display === 'none') return false
  const rect = el.getBoundingClientRect()
  return rect.height > 0 && rect.width > 0
}

const noop = () => {}

// kind: what differs between a tooltip and a popover (see TOOLTIP below and vBPopover.js)
const createTip = (el, kind) => {
  const state = {
    id: `__bv_${kind.name}_${++uid}__`,
    title: undefined,
    html: false,
    interactive: true,
    triggers: [],
    activeTrigger: { hover: false, focus: false },
    hoverState: '',
    hoverTimeout: null,
    fadeTimeout: null,
    visibleInterval: null,
    localShow: false,
    tip: null,
    popper: null,
  }

  const isWithActiveTrigger = () => state.triggers.some(trigger => state.activeTrigger[trigger])

  const clearHoverTimeout = () => {
    clearTimeout(state.hoverTimeout)
    state.hoverTimeout = null
  }

  const clearActiveTriggers = () => {
    state.activeTrigger.hover = false
    state.activeTrigger.focus = false
  }

  // the native browser tooltip must not appear next to ours: the title is kept in a data attribute meanwhile
  const fixTitle = () => {
    if(!el.hasAttribute('title')) return
    const title = el.getAttribute('title')
    el.setAttribute('title', '')
    if(title) el.setAttribute(DATA_TITLE_ATTR, title)
  }

  const restoreTitle = () => {
    if(!el.hasAttribute(DATA_TITLE_ATTR)) return
    const title = el.getAttribute(DATA_TITLE_ATTR)
    el.removeAttribute(DATA_TITLE_ATTR)
    if(title) el.setAttribute('title', title)
  }

  const describedBy = () => (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)

  const addAriaDescribedby = () => {
    el.setAttribute('aria-describedby', describedBy().concat(state.id).join(' '))
  }

  const removeAriaDescribedby = () => {
    const ids = describedBy().filter(id => id !== state.id)
    if(ids.length) el.setAttribute('aria-describedby', ids.join(' '))
    else el.removeAttribute('aria-describedby')
  }

  const setInner = () => {
    const inner = state.tip.querySelector(`.${kind.innerClass}`)
    if(state.html) inner.innerHTML = state.title
    else inner.textContent = state.title
  }

  // iOS does not fire mouseleave unless something else listens for mouse events
  const setOnTouchStartListener = (on) => {
    if(!('ontouchstart' in document.documentElement)) return
    Array.from(document.body.children).forEach(child => {
      child[on ? 'addEventListener' : 'removeEventListener']('mouseover', noop)
    })
  }

  const setWhileOpenListeners = (on) => {
    clearInterval(state.visibleInterval)
    state.visibleInterval = null
    if(on) {
      // the element was removed or hidden while its tooltip is shown (v-if, closed modal, route change)
      state.visibleInterval = setInterval(() => {
        if(state.tip && state.localShow && !isVisible(el)) forceHide()
      }, VISIBILITY_CHECK_INTERVAL)
    }
    setOnTouchStartListener(on)
  }

  // popper flips the tooltip to another side when there is no room above
  const setPlacement = (data) => {
    if(!state.tip) return
    const side = data.placement.split('-')[0]
    ;['top', 'bottom', 'left', 'right'].forEach(name => state.tip.classList.toggle(`bs-${kind.name}-${name}`, name === side))
  }

  const createTemplate = () => {
    const tip = state.tip = document.createElement('div')
    tip.id = state.id
    tip.setAttribute('role', 'tooltip')
    tip.setAttribute('tabindex', '-1')
    tip.className = `${kind.name} b-${kind.name} bs-${kind.name}-${kind.placement}`
    if(!state.interactive) tip.classList.add('noninteractive')
    // scoped styles of the component the element belongs to also apply to its tooltip
    Array.from(el.attributes).forEach(attr => {
      if(attr.name.startsWith('data-v-')) tip.setAttribute(attr.name, '')
    })
    const arrow = document.createElement('div')
    arrow.className = 'arrow'
    const inner = document.createElement('div')
    inner.className = kind.innerClass
    tip.appendChild(arrow)
    tip.appendChild(inner)
    setInner()
    ;['mouseenter', 'mouseleave', 'focusin', 'focusout'].forEach(type => tip.addEventListener(type, handleEvent))

    const container = el.closest(CONTAINER_SELECTOR) || document.body
    container.appendChild(tip)

    state.popper = new Popper(el, tip, {
      placement: kind.placement,
      modifiers: {
        offset: { offset: 0 },
        flip: { behavior: 'flip' },
        arrow: { element: '.arrow' },
        preventOverflow: { padding: BOUNDARY_PADDING, boundariesElement: 'scrollParent' }
      },
      onCreate: (data) => {
        if(data.originalPlacement !== data.placement) setPlacement(data)
      },
      onUpdate: setPlacement
    })
  }

  const destroyTemplate = () => {
    setWhileOpenListeners(false)
    clearHoverTimeout()
    clearTimeout(state.fadeTimeout)
    state.fadeTimeout = null
    state.hoverState = ''
    clearActiveTriggers()
    if(state.popper) state.popper.destroy()
    state.popper = null
    if(state.tip && state.tip.parentNode) state.tip.parentNode.removeChild(state.tip)
    state.tip = null
    removeAriaDescribedby()
    restoreTitle()
    state.localShow = false
  }

  // the "fade" and "show" classes are present only during the transitions, as with bootstrap-vue
  const fadeIn = () => {
    const tip = state.tip
    tip.classList.add('fade')
    requestAnimationFrame(() => {
      if(state.tip !== tip) return
      tip.classList.add('show')
      state.fadeTimeout = setTimeout(() => {
        if(state.tip !== tip) return
        tip.classList.remove('fade', 'show')
        const previousHoverState = state.hoverState
        state.hoverState = ''
        // the pointer left while the tooltip was appearing
        if(previousHoverState === 'out') leave(null)
      }, FADE_DURATION)
    })
  }

  const fadeOut = () => {
    const tip = state.tip
    clearTimeout(state.fadeTimeout)
    tip.classList.add('fade', 'show')
    requestAnimationFrame(() => {
      if(state.tip !== tip) return
      tip.classList.remove('show')
      state.fadeTimeout = setTimeout(() => {
        if(state.tip === tip) destroyTemplate()
      }, FADE_DURATION)
    })
  }

  const show = () => {
    if(!document.body.contains(el) || !isVisible(el) || state.title === undefined || state.title === null || state.title === '') {
      return
    }
    if(state.tip || state.localShow) return
    state.localShow = true
    fixTitle()
    addAriaDescribedby()
    createTemplate()
    setWhileOpenListeners(true)
    fadeIn()
  }

  const hide = () => {
    if(!state.tip || !state.localShow) {
      restoreTitle()
      return
    }
    setWhileOpenListeners(false)
    fadeOut()
    clearActiveTriggers()
    state.hoverState = ''
  }

  // hides at once, without the fade and regardless of the pointer
  const forceHide = () => {
    if(!state.tip || !state.localShow) return
    destroyTemplate()
  }

  const enter = (event = null) => {
    if(event) state.activeTrigger[event.type === 'focusin' ? 'focus' : 'hover'] = true
    if(state.localShow || state.hoverState === 'in') {
      state.hoverState = 'in'
      return
    }
    clearHoverTimeout()
    state.hoverState = 'in'
    fixTitle()
    state.hoverTimeout = setTimeout(() => {
      if(state.hoverState === 'in') show()
      else if(!state.localShow) restoreTitle()
    }, DELAY)
  }

  const leave = (event = null) => {
    if(event) state.activeTrigger[event.type === 'focusout' ? 'focus' : 'hover'] = false
    if(isWithActiveTrigger()) return
    clearHoverTimeout()
    state.hoverState = 'out'
    state.hoverTimeout = setTimeout(() => {
      if(state.hoverState === 'out') hide()
    }, DELAY)
  }

  function handleEvent(event) {
    if(isDisabled(el)) return
    const type = event.type
    if(type === 'mouseenter' && state.triggers.includes('hover')) {
      enter(event)
    } else if(type === 'focusin' && state.triggers.includes('focus')) {
      enter(event)
    } else if((type === 'focusout' && state.triggers.includes('focus')) || (type === 'mouseleave' && state.triggers.includes('hover'))) {
      const tip = state.tip
      const eventTarget = event.target
      const relatedTarget = event.relatedTarget
      // moving between the element and its tooltip, or inside one of them, is not leaving
      if(
          (tip && tip.contains(eventTarget) && el.contains(relatedTarget)) ||
          (tip && el.contains(eventTarget) && tip.contains(relatedTarget)) ||
          (tip && tip.contains(eventTarget) && tip.contains(relatedTarget)) ||
          (el.contains(eventTarget) && el.contains(relatedTarget))
      ) {
        return
      }
      leave(event)
    }
  }

  const EVENTS = ['mouseenter', 'mouseleave', 'focusin', 'focusout']

  return {
    listen() {
      EVENTS.forEach(type => el.addEventListener(type, handleEvent))
    },
    update({ title, html, interactive, triggers }) {
      const previousTitle = state.title
      state.title = title
      state.html = html
      state.interactive = interactive
      state.triggers = triggers
      if(title === previousTitle) return
      // Vue has just written the new title attribute: hide it again while our tooltip is in charge
      if(state.localShow || state.hoverState === 'in') fixTitle()
      if(state.tip) {
        setInner()
        if(state.popper) state.popper.scheduleUpdate()
      }
      // the title was emptied while the tooltip is shown
      if(!title) hide()
    },
    destroy() {
      EVENTS.forEach(type => el.removeEventListener(type, handleEvent))
      destroyTemplate()
    },
  }
}

// builds the directive for a tooltip or a popover
export const createTipDirective = (kind) => {
  const STATE = `__app_${kind.name}__`
  return {
    mounted(el, binding, vnode) {
      el[STATE] = createTip(el, kind)
      el[STATE].update(kind.config(binding, vnode))
      el[STATE].listen()
    },
    updated(el, binding, vnode) {
      if(el[STATE]) el[STATE].update(kind.config(binding, vnode))
    },
    beforeUnmount(el) {
      if(el[STATE]) el[STATE].destroy()
      delete el[STATE]
    },
  }
}

const TOOLTIP = {
  name: 'tooltip',
  innerClass: 'tooltip-inner',
  placement: 'top',
  config(binding, vnode) {
    const modifiers = binding.modifiers || {}
    const title = vnode.props ? vnode.props.title : undefined
    return {
      title: title === null ? undefined : title,
      html: !!modifiers.html,
      interactive: !modifiers.noninteractive,
      // without a trigger modifier bootstrap-vue uses "hover focus"
      triggers: modifiers.hover ? ['hover'] : ['hover', 'focus'],
    }
  }
}

export const vBTooltip = createTipDirective(TOOLTIP)

export default vBTooltip
